/**
 * Mobile diagnostics utility
 * Safe debug logging for iOS/TestFlight issues
 * Can be toggled via environment variable
 */

const ENABLE_DIAGNOSTICS = 
  process.env.NEXT_PUBLIC_ENABLE_MOBILE_DIAGNOSTICS === "true" ||
  (typeof window !== "undefined" && 
   (window.location.search.includes("debug=true") || 
    localStorage.getItem("enableMobileDiagnostics") === "true"));

export interface DiagnosticLog {
  timestamp: string;
  category: string;
  message: string;
  data?: any;
}

const logs: DiagnosticLog[] = [];
const MAX_LOGS = 100;

export function logDiagnostic(category: string, message: string, data?: any) {
  if (!ENABLE_DIAGNOSTICS) return;
  
  const log: DiagnosticLog = {
    timestamp: new Date().toISOString(),
    category,
    message,
    data: data ? sanitizeData(data) : undefined,
  };
  
  logs.push(log);
  if (logs.length > MAX_LOGS) {
    logs.shift();
  }
  
  // Also log to console with prefix
  console.log(`[Mobile Diagnostics] [${category}] ${message}`, data || "");
}

function sanitizeData(data: any): any {
  if (!data || typeof data !== "object") return data;
  
  const sanitized: any = Array.isArray(data) ? [] : {};
  
  for (const [key, value] of Object.entries(data)) {
    // Remove sensitive data
    if (key.toLowerCase().includes("password") || 
        key.toLowerCase().includes("secret") ||
        key.toLowerCase().includes("token") && typeof value === "string" && value.length > 20) {
      sanitized[key] = "[REDACTED]";
    } else if (typeof value === "object" && value !== null) {
      sanitized[key] = sanitizeData(value);
    } else {
      sanitized[key] = value;
    }
  }
  
  return sanitized;
}

export function getDiagnostics(): DiagnosticLog[] {
  return [...logs];
}

export function clearDiagnostics() {
  logs.length = 0;
}

export function isMobileEnvironment(): boolean {
  if (typeof window === "undefined") return false;
  
  const ua = navigator.userAgent;
  return /iPhone|iPad|iPod|iOS|Capacitor|Cordova|WebView/i.test(ua) ||
         (window.innerWidth < 768 && /Mobile/i.test(ua));
}

export function detectEnvironment(): {
  isMobile: boolean;
  isIOS: boolean;
  isTestFlight: boolean;
  userAgent: string;
  platform: string;
} {
  if (typeof window === "undefined") {
    return {
      isMobile: false,
      isIOS: false,
      isTestFlight: false,
      userAgent: "server",
      platform: "server",
    };
  }
  
  const ua = navigator.userAgent;
  const isIOS = /iPhone|iPad|iPod|iOS/i.test(ua);
  const isTestFlight = /TestFlight/i.test(ua) || ua.includes("beta");
  const isMobile = isIOS || /Android|Mobile/i.test(ua) || /Capacitor|Cordova|WebView/i.test(ua);
  
  return {
    isMobile,
    isIOS,
    isTestFlight,
    userAgent: ua,
    platform: navigator.platform || "unknown",
  };
}
